
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { selectScenarioWithShiftsById } from '@/entities/schedules'
import { publishScenario } from '@/entities/schedules/thunks/publishScenario'
import { Button, Modal } from '@/shared/components'
import { DatePicker } from '@/shared/components/DatePicker/DatePicker'
import { IconTextGridCell } from '@/shared/components/GridTable/GridCells/IconTextGridCell'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { DialogComponentProps } from '@/shared/components/Modal/models/DialogStructure'
import { TextField } from '@/shared/components/TextField/TextField'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faTruck } from '@fortawesome/pro-solid-svg-icons'
import { styled, Typography } from '@mui/material'
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium'
import { EntityId } from '@reduxjs/toolkit'

export type PublishScenarioDialogProps = DialogComponentProps & {
  onClose: () => void
}

export const PublishScenarioDialog: React.FC<PublishScenarioDialogProps> = ({
  open,
  onClose,
  scenarioId,
  initialRunIds = [],
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>(initialRunIds)
  const [publishToDate, setPublishToDate] = useState<Dayjs | null>(null)
  const { setToastContent } = useToastContext()
  const scenario = useAppSelector(state => selectScenarioWithShiftsById(state, scenarioId))

  const dispatch = useAppDispatch()

  // Run Table Column Definitions
  const runColumnDefs: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 150,
      renderCell: (params) => (<IconTextGridCell {...params} />),
    },
    {
      field: 'orders',
      headerName: 'Orders',
      width: 100,
    },
    {
      field: 'violations',
      headerName: 'Violations',
      width: 100,
    },
  ]

  // Run Table Rows
  const rows = useMemo(() => {
    if (!scenario?.shifts) return []

    return scenario.shifts
      .map(shift => {
        return {
          id: shift.runId,
          isSelectable: true,
          name: {
            text: shift.name,
            uiColor: shift.uiColor,
            icon: faTruck,
          },
          orders: shift.orderCount,
          violations: shift.violationCount,
        }
      })
  }, [scenario])

  // Initialize Publish To Date
  useEffect(() => {
    if (scenario?.dateOfOperation) {
      setPublishToDate(dayjs(scenario.dateOfOperation, 'DD/MM/YYYY'))
    }
  }, [scenario])

  // On Change Publish To Date
  const onChangePublishToDate = (date: Dayjs | null) => {
    setPublishToDate(date)
  }

  // On Submit Form
  const onSubmit = async () => {
    if (!scenario || rowSelectionModel.length === 0) return
    setIsLoading(true)

    let runIds: EntityId[] = []

    if (rowSelectionModel.length !== rows.length) {
      runIds = rowSelectionModel.map((id) => (id))
    }

    // Validate data before publishing
    if (!publishToDate) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Publish Date Required',
        content: 'Publish to date is required.',
      })
      setIsLoading(false)

      return
    }

    // Validate publish date is has the same weekday as the scenario
    const publishDateOfWeek = publishToDate.day()

    if (publishDateOfWeek !== scenario.weekday) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Invalid Publish Date',
        content: 'Publish to date must be the same day of the week as the scenario.',
      })
      setIsLoading(false)

      return
    }

    // Publish scenario
    await dispatch(
      publishScenario({
        id: scenario.id,
        dateOfOperation: publishToDate?.format('DD/MM/YYYY') || '',
        dateOfWeek: scenario.dayOfWeek,
        unassignedOrderCount: scenario.unassignedOrderCount,
        weekday: scenario.weekday,
        name: scenario.name,
        runIds: runIds,
        onSuccess: () => {
          setIsLoading(false)
          onClose()
        },
      }),
    )
    setIsLoading(false)
  }

  return (
    <Modal
      title={`Publish Scenario: ${scenario?.name}`}
      onClose={onClose}
      open={open}
      transition="opacity"
      showBackground={true}
      disableClose={isLoading}
    >
      <StyledContent>
        <StyledSectionTypography variant="body1">
          Scenario information
        </StyledSectionTypography>
        {(scenario?.unassignedOrderCount && scenario?.unassignedOrderCount > 0) ? (
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            This scenario has {scenario?.unassignedOrderCount || 0} unassigned orders.<br />
            *Select all runs to include these unassigned orders when publish.
          </Typography>
        ) : null}

        <TextField
          label="Scenario Name"
          value={scenario?.name}
          disabled
        />
        <TextField
          label="Day of Week"
          value={scenario?.dayOfWeek}
          disabled
        />
        <DatePicker
          label="Publish to"
          format="DD/MM/YYYY"
          onAccept={onChangePublishToDate}
          onChange={onChangePublishToDate}
          value={publishToDate}
          disablePast
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
            },
          }}
        />
        <StyledSectionTypography variant="body1" sx={{ marginTop: pxToRem(16) }}>
          Select runs to publish to runsheet:
        </StyledSectionTypography>
        <GridTable
          checkboxSelection
          disableVirtualization
          rows={rows}
          columns={runColumnDefs}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel)
          }}
          sx={{
            height: 340,
          }}
        />
        <StyledFormActions>
          {onClose && <Button text onClick={onClose}>Cancel</Button>}
          <Button
            primary
            onClick={onSubmit}
            disabled={rowSelectionModel.length === 0 || !publishToDate}
          >
            Save
          </Button>
        </StyledFormActions>
      </StyledContent>
    </Modal>
  )
}

const StyledContent = styled('div')`
  ${(props) => props.theme.breakpoints.up('sm')} {
    min-width: ${(props) => props.theme.typography.pxToRem(360)};
    max-width: ${(props) => props.theme.typography.pxToRem(460)};
  }
`

const StyledFormActions = styled('div')`
  width: 100%;
  padding-bottom: ${(props) => props.theme.typography.pxToRem(12)};
  display: flex;
  justify-content: flex-end;
  gap: ${(props) => props.theme.typography.pxToRem(12)};
  padding-top: ${(props) => props.theme.typography.pxToRem(8)};
  background-color: ${(props) => props.theme.palette.background.paper};
`

const StyledSectionTypography = styled(Typography)`
  font-weight: 500;
  font-size: ${(props) => props.theme.typography.pxToRem(16)};
  margin-bottom: ${(props) => props.theme.typography.pxToRem(8)};
`